import React, { Fragment, useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  Container,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Box,
  useTheme,
  useMediaQuery,
  Fab,
  Chip,
  Avatar,
  IconButton,
  Divider,
  Paper,
  Grid,
  Tooltip,
  Skeleton,
  Stack,
  Collapse,
  LinearProgress,
} from "@mui/material";
import { gql, useQuery } from "@apollo/client";
import { useKitchenDetail } from "../Context/GetMyKitchenDetailContext";
import Preloader from "../Components/Preloader/Preloader";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import BreadCrumb from "../Components/BreadCrumb";
import {
  Restaurant,
  TrendingUp,
  Schedule,
  CheckCircle,
  Cancel,
  Warning,
  FilterList,
  Sort,
  ViewModule,
  ViewList,
  Refresh,
  Assessment,
  LocalDining,
  MonitorWeight,
  Whatshot,
  FitnessCenter,
  AccessTime,
  DateRange,
  EventNote,
  ExpandMore,
  Timeline,
  TrendingDown,
  KeyboardArrowUp,
  Star,
  Celebration,
  EmojiEvents,
  LocalFireDepartment,
  Speed,
  TrendingFlat,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

const statusIcons = {
  SUCCESS: {
    icon: CheckCircle,
    color: "success",
    emoji: "✅",
    gradient: "linear-gradient(45deg, #4caf50, #8bc34a)",
    shadowColor: "rgba(76, 175, 80, 0.3)",
  },
  PENDING: {
    icon: Schedule,
    color: "warning",
    emoji: "⏳",
    gradient: "linear-gradient(45deg, #ff9800, #ffb74d)",
    shadowColor: "rgba(255, 152, 0, 0.3)",
  },
  FAILED: {
    icon: Cancel,
    color: "error",
    emoji: "❌",
    gradient: "linear-gradient(45deg, #f44336, #e57373)",
    shadowColor: "rgba(244, 67, 54, 0.3)",
  },
  PROCESSED: {
    icon: Warning,
    color: "info",
    emoji: "🔄",
    gradient: "linear-gradient(45deg, #2196f3, #64b5f6)",
    shadowColor: "rgba(33, 150, 243, 0.3)",
  },
  SCHEDULED: {
    icon: EventNote,
    color: "primary",
    emoji: "📅",
    gradient: "linear-gradient(45deg, #9c27b0, #ba68c8)",
    shadowColor: "rgba(156, 39, 176, 0.3)",
  },
  CANCELLED: {
    icon: Cancel,
    color: "error",
    emoji: "❌",
    gradient: "linear-gradient(45deg, #f44336, #e57373)",
    shadowColor: "rgba(244, 67, 54, 0.3)",
  },
};

const filterOptions = [
  { label: "Today", value: "today", icon: <DateRange />, emoji: "📅" },
  { label: "Yesterday", value: "yesterday", icon: <AccessTime />, emoji: "🕐" },
  { label: "Last 7 Days", value: "last7days", icon: <Timeline />, emoji: "📊" },
  {
    label: "This Month",
    value: "thismonth",
    icon: <CalendarMonthIcon />,
    emoji: "🗓️",
  },
  { label: "Last Month", value: "lastmonth", icon: <EventNote />, emoji: "📋" },
];

const sortOptions = [
  { label: "Latest First", value: "latest", icon: <TrendingUp />, emoji: "⬆️" },
  {
    label: "Oldest First",
    value: "oldest",
    icon: <TrendingDown />,
    emoji: "⬇️",
  },
  { label: "Recipe Name A-Z", value: "name_asc", icon: <Sort />, emoji: "🔤" },
  { label: "Recipe Name Z-A", value: "name_desc", icon: <Sort />, emoji: "🔤" },
];

const GET_COOKING_HISTORY = gql`
  query GetCookingHistory($kitchen_id: ID!, $cooking_status: CookingStatus!) {
    allCookingHistories(
      kitchen_id: $kitchen_id
      cooking_status: $cooking_status
    ) {
      id
      recipe_id
      kitchen_id
      user_id
      cooking_status
      cooking_tasks
      cook_in
      recipe_name
      created
      modified
      nutrition {
        calories
        protein
        fats
        carbohydrate
      }
    }
  }
`;

const HistoryCard = ({ record, index }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  const date = new Date(Number(record?.created));
  const formattedStartTime = date.toLocaleString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  const formattedStartDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  const statusConfig =
    statusIcons[record?.cooking_status] || statusIcons.FAILED;
  const StatusIcon = statusConfig.icon;

  // Calculate relative time
  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const recordDate = new Date(Number(timestamp));
    const diffInSeconds = Math.floor((now - recordDate) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return formattedStartDate;
  };

  // Calculate recipe score for achievement system
  const getRecipeScore = () => {
    const nutrition = record?.nutrition;
    if (!nutrition) return 0;

    const calories = nutrition.calories || 0;
    const protein = nutrition.protein || 0;
    const balanced = Math.min(calories / 500, 1) + Math.min(protein / 30, 1);
    return Math.round(balanced * 50);
  };

  const recipeScore = getRecipeScore();

  // Achievement badges based on nutrition and status
  const getAchievementBadge = () => {
    if (record?.cooking_status === "SUCCESS") {
      if (recipeScore >= 80)
        return {
          icon: <EmojiEvents />,
          color: "#FFD700",
          label: "Perfect Recipe!",
        };
      if (recipeScore >= 60)
        return { icon: <Star />, color: "#FFA500", label: "Great Job!" };
      if (recipeScore >= 40)
        return { icon: <Celebration />, color: "#32CD32", label: "Well Done!" };
    }
    return null;
  };

  const achievement = getAchievementBadge();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        delay: index * 0.1,
        duration: 0.4,
        type: "spring",
        stiffness: 100,
        damping: 15,
      }}
      whileHover={{ y: -8, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Card
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, 
                ${theme.palette.background.paper}95 0%, 
                ${theme.palette.grey[900]}90 50%,
                ${theme.palette.background.paper}95 100%)`
              : `linear-gradient(135deg, 
                ${theme.palette.background.paper}95 0%, 
                ${theme.palette.grey[50]}90 50%,
                ${theme.palette.background.paper}95 100%)`,
          border: `1px solid ${theme.palette.divider}40`,
          position: "relative",
          cursor: "pointer",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          backdropFilter: "blur(20px)",
          "&:hover": {
            transform: "translateY(-8px) scale(1.02)",
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 20px 60px rgba(0,0,0,0.4), 0 0 30px ${statusConfig.shadowColor}`
                : `0 20px 60px rgba(0,0,0,0.15), 0 0 30px ${statusConfig.shadowColor}`,
            "& .status-indicator": {
              transform: "scale(1.15) rotate(5deg)",
            },
            "& .recipe-glow": {
              opacity: 0,
            },
            "& .floating-actions": {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Animated Background Glow */}
        <Box
          className="recipe-glow"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: statusConfig.gradient,
            opacity: 0,
            transition: "opacity 0.3s ease",
            zIndex: 0,
          }}
        />

        {/* Enhanced Status Indicator Strip with Animation */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: index * 0.1 + 0.2, duration: 0.6 }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              background: statusConfig.gradient,
              boxShadow: `0 0 20px ${statusConfig.shadowColor}`,
              zIndex: 2,
            }}
          />
        </motion.div>

        {/* Achievement Badge */}
        {achievement && (
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              delay: index * 0.1 + 0.4,
              duration: 0.5,
              type: "spring",
            }}
          >
            <Tooltip title={achievement.label}>
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  zIndex: 3,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: `linear-gradient(45deg, ${achievement.color}, ${achievement.color}CC)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 4px 12px ${achievement.color}40`,
                  animation: "pulse 2s infinite",
                  "@keyframes pulse": {
                    "0%": {
                      transform: "scale(1)",
                      boxShadow: `0 4px 12px ${achievement.color}40`,
                    },
                    "50%": {
                      transform: "scale(1.05)",
                      boxShadow: `0 6px 20px ${achievement.color}60`,
                    },
                    "100%": {
                      transform: "scale(1)",
                      boxShadow: `0 4px 12px ${achievement.color}40`,
                    },
                  },
                }}
              >
                {React.cloneElement(achievement.icon, {
                  sx: {
                    fontSize: 20,
                    color: "white",
                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                  },
                })}
              </Box>
            </Tooltip>
          </motion.div>
        )}

        <CardContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box sx={{ flex: 1, mr: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                >
                  <Restaurant
                    sx={{
                      fontSize: 22,
                      color: theme.palette.primary.main,
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                    }}
                  />
                </motion.div>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    lineHeight: 1.2,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    background:
                      theme.palette.mode === "dark"
                        ? `linear-gradient(45deg, ${theme.palette.text.primary}, ${theme.palette.primary.light})`
                        : `linear-gradient(45deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
                    WebkitBackgroundClip: "text",
                    fontSize: "1.1rem",
                  }}
                >
                  {record.recipe_name}
                </Typography>
              </Box>

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Chip
                    icon={<AccessTime />}
                    label={formattedStartTime}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 28,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? `${theme.palette.info.dark}15`
                          : `${theme.palette.info.light}15`,
                      borderColor: `${theme.palette.info.main}50`,
                      color: theme.palette.info.main,
                      backdropFilter: "blur(10px)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? `${theme.palette.info.dark}25`
                            : `${theme.palette.info.light}25`,
                        transform: "scale(1.05)",
                      },
                    }}
                  />
                </motion.div>

                <motion.div whileHover={{ scale: 1.05 }}>
                  <Chip
                    icon={<CalendarMonthIcon />}
                    label={getRelativeTime(record?.created)}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 28,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? `${theme.palette.secondary.dark}15`
                          : `${theme.palette.secondary.light}15`,
                      borderColor: `${theme.palette.secondary.main}50`,
                      color: theme.palette.secondary.main,
                      backdropFilter: "blur(10px)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? `${theme.palette.secondary.dark}25`
                            : `${theme.palette.secondary.light}25`,
                        transform: "scale(1.05)",
                      },
                    }}
                  />
                </motion.div>
              </Stack>

              {/* Enhanced Nutrition Preview */}
              {record.nutrition && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexWrap: "wrap", gap: 0.5 }}
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Chip
                      icon={<Whatshot />}
                      label={`${record.nutrition.calories || 0} kcal`}
                      size="small"
                      variant="filled"
                      sx={{
                        height: 26,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        background: "linear-gradient(45deg, #ff7043, #ff5722)",
                        color: "white",
                        boxShadow: "0 2px 8px rgba(255, 87, 34, 0.3)",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(255, 87, 34, 0.4)",
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Chip
                      icon={<FitnessCenter />}
                      label={`${record.nutrition.protein || 0} g`}
                      size="small"
                      variant="filled"
                      sx={{
                        height: 26,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        background: "linear-gradient(45deg, #66bb6a, #4caf50)",
                        color: "white",
                        boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)",
                          transform: "scale(1.05)",
                        },
                      }}
                    />
                  </motion.div>
                </Stack>
              )}

              {/* Recipe Score Display */}
              {/* {recipeScore > 0 && (
                <Box
                  sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}
                >
          <Typography
                    variant="caption"
            color="text.secondary"
                    sx={{ fontWeight: 600 }}
          >
                    Recipe Score:
          </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={recipeScore}
                      sx={{
                        width: 60,
                        height: 6,
                        borderRadius: 3,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(0,0,0,0.1)",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 3,
                          background:
                            recipeScore >= 80
                              ? "linear-gradient(45deg, #FFD700, #FFA500)"
                              : recipeScore >= 60
                              ? "linear-gradient(45deg, #FFA500, #FF7043)"
                              : "linear-gradient(45deg, #FF7043, #FF5722)",
                        },
                      }}
                    />
          <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        color:
                          recipeScore >= 80
                            ? "#FFD700"
                            : recipeScore >= 60
                            ? "#FFA500"
                            : "#FF7043",
                      }}
                    >
                      {recipeScore}%
          </Typography>
        </Box>
                </Box>
              )} */}
            </Box>

            {/* Enhanced Status Indicator */}
            <motion.div
              className="status-indicator"
              whileHover={{ scale: 1.15, rotate: 5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    delay: index * 0.1 + 0.3,
                    duration: 0.5,
                    type: "spring",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      background: statusConfig.gradient,
                      border: `3px solid ${
                        theme.palette[statusConfig.color].main
                      }30`,
                      mb: 1,
                      boxShadow: `0 8px 25px ${statusConfig.shadowColor}`,
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: -3,
                        left: -3,
                        right: -3,
                        bottom: -3,
                        borderRadius: "50%",
                        background: statusConfig.gradient,
                        opacity: 0.3,
                        animation: "pulse 2s infinite",
                      },
                    }}
                  >
                    <StatusIcon
                      sx={{
                        fontSize: 30,
                        color: "white",
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                      }}
                    />
                  </Avatar>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.4 }}
                >
                  <Chip
                    label={record?.cooking_status}
                    size="small"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      textTransform: "capitalize",
                      background: statusConfig.gradient,
                      color: "white",
                      boxShadow: `0 4px 12px ${statusConfig.shadowColor}`,
                      border: "none",
                      "&:hover": {
                        boxShadow: `0 6px 16px ${statusConfig.shadowColor}`,
                      },
                    }}
                  />
                </motion.div>
              </Box>
            </motion.div>
          </Box>

          {/* Enhanced Expandable Content */}
          <Collapse in={expanded} timeout={500}>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: expanded ? 1 : 0, y: expanded ? 0 : -20 }}
              transition={{ duration: 0.3 }}
            >
              <Divider
                sx={{
                  my: 2,
                  background: `linear-gradient(90deg, transparent, ${theme.palette.divider}, transparent)`,
                  height: 2,
                }}
              />
              <Grid container spacing={2}>
                {record.nutrition && (
                  <>
                    <Grid item xs={6} sm={6}>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 2,
                            textAlign: "center",
                            borderRadius: 3,
                            background:
                              "linear-gradient(45deg, #ff7043, #ff5722)",
                            color: "white",
                            position: "relative",
                            overflow: "hidden",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background:
                                "linear-gradient(45deg, rgba(255,255,255,0.1), transparent)",
                              opacity: 0,
                              transition: "opacity 0.3s ease",
                            },
                            "&:hover::before": {
                              opacity: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 800, mb: 0.5 }}
                          >
                            {record.nutrition.calories || 0}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, opacity: 0.9 }}
                          >
                            Calories
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 2,
                            textAlign: "center",
                            borderRadius: 3,
                            background:
                              "linear-gradient(45deg, #66bb6a, #4caf50)",
                            color: "white",
                            position: "relative",
                            overflow: "hidden",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background:
                                "linear-gradient(45deg, rgba(255,255,255,0.1), transparent)",
                              opacity: 0,
                              transition: "opacity 0.3s ease",
                            },
                            "&:hover::before": {
                              opacity: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 800, mb: 0.5 }}
                          >
                            {record.nutrition.protein || 0}g
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, opacity: 0.9 }}
                          >
                            Protein
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 2,
                            textAlign: "center",
                            borderRadius: 3,
                            background:
                              "linear-gradient(45deg, #ef5350, #f44336)",
                            color: "white",
                            position: "relative",
                            overflow: "hidden",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background:
                                "linear-gradient(45deg, rgba(255,255,255,0.1), transparent)",
                              opacity: 0,
                              transition: "opacity 0.3s ease",
                            },
                            "&:hover::before": {
                              opacity: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 800, mb: 0.5 }}
                          >
                            {record.nutrition.fats || 0}g
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, opacity: 0.9 }}
                          >
                            Fats
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 2,
                            textAlign: "center",
                            borderRadius: 3,
                            background:
                              "linear-gradient(45deg, #42a5f5, #2196f3)",
                            color: "white",
                            position: "relative",
                            overflow: "hidden",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background:
                                "linear-gradient(45deg, rgba(255,255,255,0.1), transparent)",
                              opacity: 0,
                              transition: "opacity 0.3s ease",
                            },
                            "&:hover::before": {
                              opacity: 1,
                            },
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 800, mb: 0.5 }}
                          >
                            {record.nutrition.carbohydrate || 0}g
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, opacity: 0.9 }}
                          >
                            Carbs
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                  </>
                )}
              </Grid>
              <Box sx={{ mt: 3, textAlign: "center" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontStyle: "italic",
                    "&::before": { content: '"✨ "' },
                    "&::after": { content: '" ✨"' },
                  }}
                >
                  Cooked with love on {formattedStartDate}
                </Typography>
              </Box>
            </motion.div>
          </Collapse>

          {/* Enhanced Expand/Collapse Icon */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.05)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${theme.palette.divider}30`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.1)",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <motion.div
                  animate={{ rotate: expanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ExpandMore sx={{ color: theme.palette.text.secondary }} />
                </motion.div>
              </IconButton>
            </motion.div>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const NutritionCard = ({ quantity, unit, title, state, setState, icon }) => {
  const theme = useTheme();
  const handleFilterValueChange = (e) => {
    setState(e.target.value || "All");
  };

  const IconComponent = icon || Assessment;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
    >
      <Card
        sx={{
          position: "relative",
          borderRadius: 3,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
              : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 8px 32px rgba(0,0,0,0.3)`
                : `0 8px 32px rgba(0,0,0,0.12)`,
            transform: "translateY(-2px)",
          },
        }}
      >
        {/* Filter Dropdown */}
        <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}>
          <FormControl size="small" variant="outlined">
            <Select
              value={state}
              onChange={handleFilterValueChange}
              sx={{
                fontSize: "0.75rem",
                minWidth: 90,
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiSelect-select": {
                  py: 0.5,
                  px: 1,
                },
                "&:hover": {
                  bgcolor: theme.palette.primary.dark,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 2,
                    mt: 1,
                    boxShadow: theme.shadows[8],
                  },
                },
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="lastWeek">Last Week</MenuItem>
              <MenuItem value="lastMonth">Last Month</MenuItem>
              <MenuItem value="lastYear">Last Year</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <CardContent sx={{ pt: 3, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            {/* Icon */}
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: `${theme.palette.primary.main}15`,
                mb: 2,
                border: `2px solid ${theme.palette.primary.main}20`,
              }}
            >
              <IconComponent
                sx={{ fontSize: 32, color: theme.palette.primary.main }}
              />
            </Avatar>

            {/* Value */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: "2.5rem",
                color: theme.palette.primary.main,
                lineHeight: 1,
                mb: 0.5,
                background:
                  theme.palette.mode === "dark"
                    ? `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`
                    : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {quantity}{" "}
              {unit && (
                <Typography
                  variant="span"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    mb: 1,
                  }}
                >
                  {unit}
                </Typography>
              )}
            </Typography>

            {/* Unit */}

            {/* Title */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: "1.1rem",
              }}
            >
              {title}
            </Typography>

            {/* Decorative Elements */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Enhanced Loading Skeleton Component
const CookingHistoryCardSkeleton = () => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <LinearProgress sx={{ height: 4 }} />
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1, mr: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="text" width="70%" height={32} />
            </Box>
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
              <Skeleton variant="text" width={80} height={20} />
              <Skeleton variant="text" width={100} height={20} />
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", gap: 0.5 }}
            >
              <Skeleton variant="rounded" width={80} height={24} />
              <Skeleton variant="rounded" width={90} height={24} />
            </Stack>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Skeleton
              variant="circular"
              width={56}
              height={56}
              sx={{ mb: 1 }}
            />
            <Skeleton variant="rounded" width={70} height={24} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Enhanced Statistics Card Skeleton
const StatCardSkeleton = () => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent sx={{ pt: 3, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Skeleton variant="circular" width={64} height={64} sx={{ mb: 2 }} />
          <Skeleton variant="text" width={80} height={40} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={40} height={16} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={60} height={24} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default function CookingHistoryPage() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("latest");
  const [viewMode, setViewMode] = useState("grid");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageRecords, setPageRecords] = useState([]);
  const [recordsPerPage, setRecordsPerPage] = useState(12);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const { kitchenDetail } = useKitchenDetail();

  const { loading, data, refetch } = useQuery(GET_COOKING_HISTORY, {
    variables: {
      kitchen_id: kitchenDetail?.kitchenId,
      cooking_status: statusFilter,
    },
    skip: !kitchenDetail?.kitchenId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const sortRecords = useMemo(() => {
    return (data, sortType) => {
      if (!Array.isArray(data)) return [];

      const sortedData = [...data];

      switch (sortType) {
        case "latest":
          return sortedData.sort(
            (a, b) => Number(b.created) - Number(a.created)
          );
        case "oldest":
          return sortedData.sort(
            (a, b) => Number(a.created) - Number(b.created)
          );
        case "name_asc":
          return sortedData.sort((a, b) =>
            (a.recipe_name || "").localeCompare(b.recipe_name || "")
          );
        case "name_desc":
          return sortedData.sort((a, b) =>
            (b.recipe_name || "").localeCompare(a.recipe_name || "")
          );
        default:
          return sortedData.sort(
            (a, b) => Number(b.created) - Number(a.created)
          );
      }
    };
  }, []);

  useEffect(() => {
    if (data?.allCookingHistories?.length) {
      const sortedRecords = sortRecords(data.allCookingHistories, sortOrder);
      setRecords(sortedRecords);
    }
  }, [data?.allCookingHistories, sortOrder, sortRecords]);

  useEffect(() => {
    const filteredData = filterByDate(records, dateFilter);
    const sortedFilteredData = sortRecords(filteredData, sortOrder);
    const total = Math.ceil(sortedFilteredData.length / recordsPerPage);
    setTotalPages(total);
    const slicedData = sortedFilteredData.slice(
      (page - 1) * recordsPerPage,
      page * recordsPerPage
    );
    setPageRecords(slicedData);
  }, [records, page, dateFilter, recordsPerPage, sortOrder, sortRecords]);

  const filterByDate = (data, filterOption) => {
    if (!Array.isArray(data)) return [];

    const now = new Date();
    const todayStart = new Date(now.setHours(0, 0, 0, 0)).getTime();
    const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
    const last7DaysStart = todayStart - 7 * 24 * 60 * 60 * 1000;

    const firstDayOfThisMonth = new Date(
      now.getFullYear(),
      now.getMonth(),
      1
    ).getTime();
    const firstDayOfLastMonth = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      1
    ).getTime();
    const lastDayOfLastMonth = new Date(
      now.getFullYear(),
      now.getMonth(),
      0
    ).getTime();

    return data.filter(({ created }) => {
      const createdDate = parseInt(created, 10);

      switch (filterOption) {
        case "today":
          return createdDate >= todayStart;
        case "yesterday":
          return createdDate >= yesterdayStart && createdDate < todayStart;
        case "last7days":
          return createdDate >= last7DaysStart;
        case "thismonth":
          return createdDate >= firstDayOfThisMonth;
        case "lastmonth":
          return (
            createdDate >= firstDayOfLastMonth &&
            createdDate <= lastDayOfLastMonth
          );
        default:
          return true;
      }
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (statusFilter || dateFilter) {
      setPage(1);
    }
  }, [dateFilter, statusFilter]);

  const calculateNutrition = useMemo(() => {
    return (type, filterOption) => {
      let total = 0;
      const now = new Date();

      data?.allCookingHistories?.forEach((item) => {
        const createdDate = new Date(Number(item?.created));

        let isValid = false;
        switch (filterOption) {
          case "today": {
            isValid = createdDate.toDateString() === now.toDateString();
            break;
          }
          case "yesterday": {
            const yesterday = new Date();
            yesterday.setDate(now.getDate() - 1);
            isValid = createdDate.toDateString() === yesterday.toDateString();
            break;
          }
          case "lastWeek": {
            const lastWeekStart = new Date();
            lastWeekStart.setDate(now.getDate() - 7);
            isValid = createdDate >= lastWeekStart;
            break;
          }
          case "lastMonth": {
            const lastMonthStart = new Date();
            lastMonthStart.setMonth(now.getMonth() - 1);
            isValid = createdDate >= lastMonthStart;
            break;
          }
          case "lastYear": {
            const lastYearStart = new Date();
            lastYearStart.setFullYear(now.getFullYear() - 1);
            isValid = createdDate >= lastYearStart;
            break;
          }
          case "All":
          default:
            isValid = true;
        }

        if (isValid && item?.cooking_status === "SUCCESS") {
          total += item?.nutrition?.[type] || 0;
        }
      });

      return total;
    };
  }, [data?.allCookingHistories]);

  const calculateByStatus = useMemo(() => {
    return (status, filterOption) => {
      let total = 0;
      const now = new Date();

      data?.allCookingHistories?.forEach((item) => {
        const createdDate = new Date(Number(item?.created));

        let isValid = false;
        switch (filterOption) {
          case "today": {
            isValid = createdDate.toDateString() === now.toDateString();
            break;
          }
          case "yesterday": {
            const yesterday = new Date();
            yesterday.setDate(now.getDate() - 1);
            isValid = createdDate.toDateString() === yesterday.toDateString();
            break;
          }
          case "lastWeek": {
            const lastWeekStart = new Date();
            lastWeekStart.setDate(now.getDate() - 7);
            isValid = createdDate >= lastWeekStart;
            break;
          }
          case "lastMonth": {
            const lastMonthStart = new Date();
            lastMonthStart.setMonth(now.getMonth() - 1);
            isValid = createdDate >= lastMonthStart;
            break;
          }
          case "lastYear": {
            const lastYearStart = new Date();
            lastYearStart.setFullYear(now.getFullYear() - 1);
            isValid = createdDate >= lastYearStart;
            break;
          }
          case "All":
          default:
            isValid = true;
        }

        if (isValid && item?.cooking_status === status) {
          total += 1;
        }
      });

      return total;
    };
  }, [data?.allCookingHistories]);

  const [filterCooked, setFilterCooked] = useState("All");
  const [filterCancelled, setFilterCancelled] = useState("All");
  const [filterCarbs, setFilterCarbs] = useState("All");
  const [filterFats, setFilterFats] = useState("All");
  const [filterCalorie, setFilterCalorie] = useState("All");
  const [filterProtein, setFilterProtein] = useState("All");

  return (
    <Fragment>
      <BreadCrumb
        links={[{ label: "Dashboard", href: "/dashboard" }]}
        current="Cooking History"
      />

      {loading && !data && <Preloader />}

      {!loading && (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Enhanced Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={12}
              sx={{
                p: 4,
                mb: 4,
                borderRadius: 4,
                background:
                  theme.palette.mode === "dark"
                    ? `linear-gradient(135deg, 
                      ${theme.palette.primary.dark}15 0%, 
                      ${theme.palette.background.paper} 20%,
                      ${theme.palette.background.paper} 80%,
                      ${theme.palette.secondary.dark}15 100%)`
                    : `linear-gradient(135deg, 
                      ${theme.palette.primary.light}15 0%, 
                      ${theme.palette.background.paper} 20%,
                      ${theme.palette.background.paper} 80%,
                      ${theme.palette.secondary.light}15 100%)`,
                border: `1px solid ${theme.palette.divider}`,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 0.05,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${theme.palette.primary.main.slice(
                    1
                  )}' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <motion.div
                    animate={{
                      rotate: [0, 5, -5, 0],
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      repeatDelay: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
                      }}
                    >
                      <Restaurant sx={{ fontSize: 40, color: "white" }} />
                    </Avatar>
                  </motion.div>
                  <Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 900,
                        fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                        background:
                          theme.palette.mode === "dark"
                            ? `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`
                            : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        lineHeight: 1.2,
                        mb: 0.5,
                      }}
                    >
                      Cooking History
                    </Typography>
                    <Typography
                      variant="h5"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: "1rem", sm: "1.2rem" },
                        fontWeight: 500,
                      }}
                    >
                      Track Your Culinary Journey
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Tooltip title="Refresh Data">
                      <IconButton
                        onClick={handleRefresh}
                        disabled={refreshing}
                        sx={{
                          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          color: "white",
                          width: 56,
                          height: 56,
                          boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                          "&:hover": {
                            background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                            boxShadow: `0 12px 40px ${theme.palette.primary.main}60`,
                          },
                        }}
                      >
                        <Refresh sx={{ fontSize: 28 }} />
                      </IconButton>
                    </Tooltip>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Tooltip
                      title={`Switch to ${
                        viewMode === "grid" ? "List" : "Grid"
                      } View`}
                    >
                      <IconButton
                        onClick={() =>
                          setViewMode(viewMode === "grid" ? "list" : "grid")
                        }
                        sx={{
                          background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                          color: "white",
                          width: 56,
                          height: 56,
                          boxShadow: `0 4px 16px ${theme.palette.secondary.main}40`,
                          "&:hover": {
                            background: `linear-gradient(45deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`,
                            boxShadow: `0 12px 40px ${theme.palette.secondary.main}60`,
                          },
                        }}
                      >
                        {viewMode === "grid" ? (
                          <ViewList sx={{ fontSize: 28 }} />
                        ) : (
                          <ViewModule sx={{ fontSize: 28 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </motion.div>
                </Box>
              </Box>
            </Paper>
          </motion.div>

          {/* Enhanced Filters Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Paper
              elevation={6}
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 4,
                background:
                  theme.palette.mode === "dark"
                    ? `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[900]}50 100%)`
                    : `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[50]}50 100%)`,
                border: `1px solid ${theme.palette.divider}30`,
                backdropFilter: "blur(20px)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(45deg, transparent, ${theme.palette.primary.main}05, transparent)`,
                },
              }}
            >
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        boxShadow: `0 4px 16px ${theme.palette.primary.main}30`,
                      }}
                    >
                      <FilterList sx={{ color: "white", fontSize: 24 }} />
                    </Avatar>
                  </motion.div>
                  <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                      Filters & Sorting
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ opacity: 0.8 }}
                    >
                      Customize your view with advanced filtering options
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <motion.div whileHover={{ scale: 1.02 }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel sx={{ fontWeight: 600 }}>
                          Status Filter
                        </InputLabel>
                        <Select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          label="Status Filter"
                          sx={{
                            borderRadius: 3,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: `${theme.palette.primary.main}50`,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.primary.main,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.primary.main,
                              borderWidth: 2,
                            },
                          }}
                        >
                          <MenuItem value="ALL">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Assessment color="action" fontSize="small" />
                              All Status
                            </Box>
                          </MenuItem>
                          <MenuItem value="SUCCESS">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CheckCircle color="success" fontSize="small" />
                              Success
                            </Box>
                          </MenuItem>
                          <MenuItem value="SCHEDULED">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Schedule color="primary" fontSize="small" />
                              Scheduled
                            </Box>
                          </MenuItem>
                          <MenuItem value="CANCELLED">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Cancel color="error" fontSize="small" />
                              Cancelled
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </motion.div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <motion.div whileHover={{ scale: 1.02 }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel sx={{ fontWeight: 600 }}>
                          Date Range
                        </InputLabel>
                        <Select
                          value={dateFilter}
                          onChange={(e) => setDateFilter(e.target.value)}
                          label="Date Range"
                          sx={{
                            borderRadius: 3,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: `${theme.palette.secondary.main}50`,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.secondary.main,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.secondary.main,
                              borderWidth: 2,
                            },
                          }}
                        >
                          <MenuItem value="ALL">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <DateRange color="action" fontSize="small" />
                              All Time
                            </Box>
                          </MenuItem>
                          {filterOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {React.cloneElement(option.icon, {
                                  fontSize: "small",
                                  color: "action",
                                })}
                                <span>{option.emoji}</span>
                                {option.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </motion.div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <motion.div whileHover={{ scale: 1.02 }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel sx={{ fontWeight: 600 }}>
                          Sort By
                        </InputLabel>
                        <Select
                          value={sortOrder}
                          onChange={(e) => setSortOrder(e.target.value)}
                          label="Sort By"
                          sx={{
                            borderRadius: 3,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: `${theme.palette.warning.main}50`,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.warning.main,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.warning.main,
                              borderWidth: 2,
                            },
                          }}
                        >
                          {sortOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {React.cloneElement(option.icon, {
                                  fontSize: "small",
                                  color: "action",
                                })}
                                <span>{option.emoji}</span>
                                {option.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </motion.div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <motion.div whileHover={{ scale: 1.02 }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel sx={{ fontWeight: 600 }}>
                          Items Per Page
                        </InputLabel>
                        <Select
                          value={recordsPerPage}
                          onChange={(e) => setRecordsPerPage(e.target.value)}
                          label="Items Per Page"
                          sx={{
                            borderRadius: 3,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: `${theme.palette.info.main}50`,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.info.main,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.info.main,
                              borderWidth: 2,
                            },
                          }}
                        >
                          <MenuItem value={6}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Speed fontSize="small" color="action" />6 items
                              (Fast)
                            </Box>
                          </MenuItem>
                          <MenuItem value={12}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <TrendingFlat fontSize="small" color="action" />
                              12 items (Default)
                            </Box>
                          </MenuItem>
                          <MenuItem value={24}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <TrendingUp fontSize="small" color="action" />
                              24 items (More)
                            </Box>
                          </MenuItem>
                          <MenuItem value={50}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <LocalFireDepartment
                                fontSize="small"
                                color="action"
                              />
                              50 items (All)
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </motion.div>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </motion.div>

          {/* Enhanced Statistics Section */}
          {statusFilter === "ALL" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 180, 360],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      repeatDelay: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.info.main})`,
                        boxShadow: `0 4px 20px ${theme.palette.success.main}30`,
                      }}
                    >
                      <Assessment sx={{ color: "white", fontSize: 28 }} />
                    </Avatar>
                  </motion.div>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      sx={{
                        background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.info.main})`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: 0.5,
                      }}
                    >
                      Statistics Overview
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ opacity: 0.8 }}
                    >
                      Track your cooking progress and nutritional insights
                    </Typography>
                  </Box>
                </Box>

                {loading ? (
                  <Grid container spacing={3}>
                    {Array.from({ length: 6 }).map((_, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                        <StatCardSkeleton />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                      <NutritionCard
                        quantity={calculateByStatus("SUCCESS", filterCooked)}
                        unit=""
                        title="Cooked"
                        state={filterCooked}
                        setState={setFilterCooked}
                        icon={LocalDining}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                      <NutritionCard
                        quantity={calculateByStatus(
                          "CANCELLED",
                          filterCancelled
                        )}
                        unit=""
                        title="Cancelled"
                        state={filterCancelled}
                        setState={setFilterCancelled}
                        icon={Cancel}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                      <NutritionCard
                        quantity={calculateNutrition("calories", filterCalorie)}
                        unit="kcal"
                        title="Calories"
                        state={filterCalorie}
                        setState={setFilterCalorie}
                        icon={Whatshot}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                      <NutritionCard
                        quantity={calculateNutrition("protein", filterProtein)}
                        unit="g"
                        title="Protein"
                        state={filterProtein}
                        setState={setFilterProtein}
                        icon={FitnessCenter}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                      <NutritionCard
                        quantity={calculateNutrition("fats", filterFats)}
                        unit="g"
                        title="Fats"
                        state={filterFats}
                        setState={setFilterFats}
                        icon={MonitorWeight}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                      <NutritionCard
                        quantity={calculateNutrition(
                          "carbohydrate",
                          filterCarbs
                        )}
                        unit="g"
                        title="Carbs"
                        state={filterCarbs}
                        setState={setFilterCarbs}
                        icon={Restaurant}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </motion.div>
          )}

          {/* Enhanced Content Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            {loading ? (
              <Grid container spacing={3}>
                {Array.from({ length: recordsPerPage }).map((_, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={viewMode === "grid" ? 6 : 12}
                    md={viewMode === "grid" ? 4 : 12}
                    lg={viewMode === "grid" ? 3 : 12}
                    key={index}
                  >
                    <CookingHistoryCardSkeleton />
                  </Grid>
                ))}
              </Grid>
            ) : !pageRecords?.length ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Paper
                  elevation={4}
                  sx={{
                    p: 6,
                    borderRadius: 4,
                    textAlign: "center",
                    background:
                      theme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[900]})`
                        : `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[50]})`,
                    border: `2px dashed ${theme.palette.divider}`,
                  }}
                >
                  <motion.div
                    animate={{
                      rotate: [0, 10, -10, 0],
                      scale: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 1,
                    }}
                  >
                    <Restaurant
                      sx={{
                        fontSize: 80,
                        color: theme.palette.text.secondary,
                        opacity: 0.5,
                        mb: 2,
                      }}
                    />
                  </motion.div>
                  <Typography
                    variant="h4"
                    gutterBottom
                    sx={{ fontWeight: 700, mb: 2 }}
                  >
                    No Cooking History Found
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {statusFilter !== "ALL" || dateFilter !== "ALL"
                      ? "Try adjusting your filters to see more results 🔍"
                      : "Start cooking some recipes to see your history here! 👨‍🍳"}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ opacity: 0.7 }}
                  >
                    Your culinary journey awaits! ✨
                  </Typography>
                </Paper>
              </motion.div>
            ) : (
              <>
                {/* Results Header */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      mb: 3,
                      borderRadius: 3,
                      background:
                        theme.palette.mode === "dark"
                          ? `linear-gradient(90deg, ${theme.palette.background.paper}, ${theme.palette.grey[900]})`
                          : `linear-gradient(90deg, ${theme.palette.background.paper}, ${theme.palette.grey[50]})`,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          "&::before": { content: '"📊 "' },
                        }}
                      >
                        Showing {pageRecords.length} of {records.length} recipes
                      </Typography>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Chip
                          label={`Sorted by: ${
                            sortOptions.find((opt) => opt.value === sortOrder)
                              ?.emoji
                          } ${
                            sortOptions.find((opt) => opt.value === sortOrder)
                              ?.label
                          }`}
                          variant="outlined"
                          size="medium"
                          sx={{
                            fontWeight: 600,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            bgcolor: `${theme.palette.primary.main}10`,
                          }}
                        />
                      </motion.div>
                    </Box>
                  </Paper>
                </motion.div>

                {/* Recipe Cards Grid */}
                <AnimatePresence mode="wait">
                  <Grid container spacing={3}>
                    {pageRecords.map((record, index) => (
                      <Grid
                        item
                        xs={12}
                        sm={viewMode === "grid" ? 6 : 12}
                        md={viewMode === "grid" ? 4 : 12}
                        lg={viewMode === "grid" ? 3 : 12}
                        key={record.id}
                      >
                        <HistoryCard record={record} index={index} />
                      </Grid>
                    ))}
                  </Grid>
                </AnimatePresence>

                {/* Enhanced Pagination */}
                {totalPages > 1 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                  >
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 6 }}
                    >
                      <Paper
                        elevation={8}
                        sx={{
                          p: 3,
                          borderRadius: 4,
                          background:
                            theme.palette.mode === "dark"
                              ? `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[900]})`
                              : `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[50]})`,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(e, value) => {
                            setPage(value);
                            scrollToTop();
                          }}
                          color="primary"
                          size={isSmallScreen ? "medium" : "large"}
                          showFirstButton
                          showLastButton
                          siblingCount={isSmallScreen ? 0 : 2}
                        />
                      </Paper>
                    </Box>
                  </motion.div>
                )}
              </>
            )}
          </motion.div>

          {/* Enhanced Scroll to Top */}
          <AnimatePresence>
            {showScrollTop && (
              <motion.div
                initial={{ opacity: 0, scale: 0, rotate: -180 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0, rotate: 180 }}
                transition={{ duration: 0.3, type: "spring" }}
                style={{
                  position: "fixed",
                  bottom: 24,
                  right: 24,
                  zIndex: 1000,
                }}
              >
                <Tooltip title="Back to Top">
                  <Fab
                    color="primary"
                    size="large"
                    onClick={scrollToTop}
                    sx={{
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
                      width: 64,
                      height: 64,
                      "&:hover": {
                        transform: "scale(1.1)",
                      },
                      transition: "transform 0.2s ease",
                    }}
                  >
                    <motion.div
                      animate={{ y: [-2, 2, -2] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <KeyboardArrowUp sx={{ fontSize: 32, color: "white" }} />
                    </motion.div>
                  </Fab>
                </Tooltip>
              </motion.div>
            )}
          </AnimatePresence>
        </Container>
      )}
    </Fragment>
  );
}
